import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import Reveal from "../../components/Reveal";
import AutoInternalLinks from "../../components/AutoInternalLinks";
import axiosInstance from "../../api/axiosInstance";
import {
  MdInfoOutline,
  MdCollectionsBookmark,
  MdSettingsSuggest,
  MdCookie,
  MdLock,
  MdShare,
  MdVerifiedUser,
  MdChildCare,
  MdUpdateDisabled,
  MdContactMail,
} from "react-icons/md";

// Icon mapping helper
const IconMap = {
  MdInfoOutline: <MdInfoOutline />,
  MdCollectionsBookmark: <MdCollectionsBookmark />,
  MdSettingsSuggest: <MdSettingsSuggest />,
  MdCookie: <MdCookie />,
  MdLock: <MdLock />,
  MdShare: <MdShare />,
  MdVerifiedUser: <MdVerifiedUser />,
  MdChildCare: <MdChildCare />,
  MdUpdateDisabled: <MdUpdateDisabled />,
  MdContactMail: <MdContactMail />,
};

const getIcon = (iconName) => {
  return IconMap[iconName] || <MdInfoOutline />;
};

// Default static Privacy Policy fallback
const DEFAULT_PRIVACY_POLICY = {
  title: "Privacy Policy",
  description: "Welcome to Novarsis Technology. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.",
  lastUpdated: "January 1, 2024",
  metaTitle: "Privacy Policy | Novarsis Technology",
  metaDescription: "Read the Privacy Policy of Novarsis Technology to understand how we collect, use, and protect your personal information.",
  metaKeywords: "Novarsis Technology privacy policy, data protection, cookies policy, user rights",
  ogTitle: "Privacy Policy - Novarsis Technology",
  ogDescription: "Learn how Novarsis Technology protects your privacy and handles your personal data.",
  navigationItems: [
    { id: "nav-1", title: "Introduction", sortOrder: 0 },
    { id: "nav-2", title: "Information We Collect", sortOrder: 1 },
    { id: "nav-3", title: "How We Use Your Information", sortOrder: 2 },
    { id: "nav-4", title: "Cookies & Tracking Technologies", sortOrder: 3 },
    { id: "nav-5", title: "Data Storage & Security", sortOrder: 4 },
    { id: "nav-6", title: "Third-Party Services", sortOrder: 5 },
    { id: "nav-7", title: "Your Rights", sortOrder: 6 },
    { id: "nav-8", title: "Children's Privacy", sortOrder: 7 },
    { id: "nav-9", title: "Changes to This Privacy Policy", sortOrder: 8 },
    { id: "nav-10", title: "Contact Information", sortOrder: 9 },
  ],
  sections: [
    {
      id: "sec-1",
      sectionNumber: "1",
      icon: "MdInfoOutline",
      title: "Introduction",
      content: "<p>Welcome to Novarsis Technology. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.</p><p>Please read this policy carefully. If you disagree with its terms, please discontinue use of our site. We reserve the right to make changes to this policy at any time, so please review it frequently.</p>"
    },
    {
      id: "sec-2",
      sectionNumber: "2",
      icon: "MdCollectionsBookmark",
      title: "Information We Collect",
      content: "<p>We may collect information about you in a variety of ways, including:</p><p><strong>Personal Data:</strong> Name, email address, phone number, and other contact details you voluntarily provide when contacting us or applying for a job.</p><p><strong>Device & Usage Data:</strong> IP address, browser type, operating system, referring URLs, pages visited, and time spent on pages.</p><p><strong>Form Submissions:</strong> Information submitted through our contact forms, job application forms, or enquiry forms.</p><p><strong>Communication Data:</strong> Messages you send us via email or other communication channels.</p>"
    },
    {
      id: "sec-3",
      sectionNumber: "3",
      icon: "MdSettingsSuggest",
      title: "How We Use Your Information",
      content: "<p>Having accurate information about you allows us to provide a smooth, efficient, and personalised experience. Specifically, we may use information collected about you to:</p><ul><li>Respond to enquiries</li><li>Process applications</li><li>Improve services</li><li>Send updates (with consent)</li><li>Prevent fraud</li><li>Comply with legal obligations</li></ul>"
    },
    {
      id: "sec-4",
      sectionNumber: "4",
      icon: "MdCookie",
      title: "Cookies & Tracking Technologies",
      content: "<p>We use cookies and similar tracking technologies to improve your browsing experience and analyse site traffic. Cookies are small data files placed on your device when you visit a website.</p><p><strong>Essential Cookies:</strong> Required for the website to function properly.</p><p><strong>Analytics Cookies:</strong> Help us understand how visitors interact with the site (e.g., Google Analytics).</p><p><strong>Marketing Cookies:</strong> Used to deliver personalised advertisements (e.g., Meta Pixel).</p><p>Cookies improve website functionality and users can manage them through browser settings.</p>"
    },
    {
      id: "sec-5",
      sectionNumber: "5",
      icon: "MdLock",
      title: "Data Storage & Security",
      content: "<p>We implement industry-standard administrative, technical, and physical security measures to protect your personal information from unauthorised access, disclosure, alteration, or destruction. However, please note that no method of transmission over the internet or electronic storage is 100% secure.</p>"
    },
    {
      id: "sec-6",
      sectionNumber: "6",
      icon: "MdShare",
      title: "Third-Party Services",
      content: "<p>We may share your information with trusted third-party service providers who assist us in operating our website and conducting our business, including:</p><ul><li>Cloud Hosting Providers</li><li>Email Platforms</li><li>Google Analytics</li><li>Social Media Platforms</li></ul><p>We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties for their marketing purposes.</p>"
    },
    {
      id: "sec-7",
      sectionNumber: "7",
      icon: "MdVerifiedUser",
      title: "Your Rights",
      content: "<p>Depending on your location, you may have the following rights regarding your personal data:</p><ul><li>Right to Access</li><li>Right to Rectification</li><li>Right to Erasure</li><li>Right to Restriction</li><li>Right to Object</li><li>Right to Data Portability</li></ul>"
    },
    {
      id: "sec-8",
      sectionNumber: "8",
      icon: "MdChildCare",
      title: "Children's Privacy",
      content: "<p>Our website and services are not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately for data removal.</p>"
    },
    {
      id: "sec-9",
      sectionNumber: "9",
      icon: "MdUpdateDisabled",
      title: "Changes to This Privacy Policy",
      content: "<p>We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any significant changes by updating the 'Last Updated' date at the top of this page. We encourage you to review this Privacy Policy periodically to stay informed.</p>"
    },
    {
      id: "sec-10",
      sectionNumber: "10",
      icon: "MdContactMail",
      title: "Contact Information",
      content: "<p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please do not hesitate to reach out to us:</p>"
    }
  ],
  contactInfo: {
    email: "info@novarsistech.com",
    phone: "+91 91117 20303",
    address: "301, 3rd Floor, Vikram Urban, Vijay Nagar, Indore, MP 452010",
    businessHours: "Mon – Sat: 10:00 AM – 6:00 PM"
  }
};

const PrivacyPolicy = () => {
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const response = await axiosInstance.get("/privacy-policy");
        if (
          response.data.success &&
          response.data.data &&
          response.data.data.sections &&
          response.data.data.sections.length > 0
        ) {
          setPolicyData(response.data.data);
        } else {
          // API returned success but no valid data - use fallback
          setPolicyData(DEFAULT_PRIVACY_POLICY);
        }
      } catch (err) {
        console.error("Error fetching Privacy Policy:", err);
        // API failed - use fallback silently
        setPolicyData(DEFAULT_PRIVACY_POLICY);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicy();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="w-16 h-16 border-4 border-[#008300] border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600 font-medium">Loading Privacy Policy...</p>
      </div>
    );
  }

  const {
    title,
    description,
    lastUpdated,
    metaTitle,
    metaDescription,
    metaKeywords,
    ogTitle,
    ogDescription,
    sections = [],
    navigationItems = [],
    contactInfo,
  } = policyData;

  return (
    <>
      {/* ===== SEO HEAD ===== */}
      <Helmet>
        <title>{metaTitle || `${title} | Novarsis Technology`}</title>
        <meta
          name="description"
          content={metaDescription || "Read the Privacy Policy of Novarsis Technology to understand how we collect, use, and protect your personal information."}
        />
        <meta
          name="keywords"
          content={metaKeywords || "Novarsis Technology privacy policy, data protection, cookies policy, user rights"}
        />
        <link rel="canonical" href="https://novarsistech.com/privacy-policy" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={ogTitle || metaTitle || title}
        />
        <meta
          property="og:description"
          content={ogDescription || metaDescription || description}
        />
        <meta
          property="og:url"
          content="https://novarsistech.com/privacy-policy"
        />
      </Helmet>

      {/* ===== HERO SECTION ===== */}
      <section className="relative w-full h-[40vh] flex items-center justify-center overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/Images/banner.mp4"
          autoPlay
          loop
          muted
          playsInline
          poster="/Images/bannerImage.png"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 to-black/35" />

        {/* Decorative floating circles */}
        <div className="absolute top-6 left-10 w-24 h-24 rounded-full border border-[#008300]/30 opacity-40" />
        <div className="absolute bottom-8 right-12 w-16 h-16 rounded-full border border-[#008300]/20 opacity-30" />
        <div className="absolute top-1/3 right-1/4 w-8 h-8 rounded-full bg-[#008300]/20 blur-sm" />

        {/* Content */}
        <div className="relative z-10 text-center text-white px-4 sm:px-6">
          <Reveal>
            <p className="text-gray-300 text-xs sm:text-sm md:text-base lg:text-xl mb-2 md:mb-3">
              <Link to="/" className="hover:text-[#008300] transition">
                Home
              </Link>
              <span className="text-[#008300] mx-1">/</span> Privacy Policy
            </p>
          </Reveal>

          <Reveal>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight drop-shadow-lg">
              {title}
            </h1>
          </Reveal>

          <Reveal>
            <p className="mt-3 md:mt-4 max-w-2xl mx-auto text-gray-200 text-sm sm:text-base md:text-lg">
              {description}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ===== LAST UPDATED BANNER ===== */}
      <div className="bg-[#f0faf0] border-b border-[#008300]/20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-[#008300]">Last Updated:</span>{" "}
            {lastUpdated}
          </p>
          <p className="text-sm text-gray-500">
            Questions?{" "}
            <Link
              to="/contact"
              className="text-[#008300] font-medium hover:underline"
            >
              Contact us
            </Link>
          </p>
        </div>
      </div>

      {/* ===== MAIN CONTENT ===== */}
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">

          {/* Quick Navigation */}
          {navigationItems.length > 0 && (
            <Reveal>
              <div className="mb-14">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 border-l-4 border-[#008300] pl-3">
                  Quick Navigation
                </h2>
                <div className="flex flex-wrap gap-2">
                  {navigationItems
                    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                    .map((s, idx) => (
                    <a
                      key={s.id || idx}
                      href={`#sec-${idx}`}
                      className="text-xs sm:text-sm px-3 py-1.5 rounded-full border border-[#008300]/30 text-[#008300] hover:bg-[#008300] hover:text-white transition-all duration-200 font-medium"
                    >
                      {s.title}
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          {/* Policy Section Cards */}
          <div className="space-y-8">
            {sections
              .sort((a, b) => {
                // Sort by sectionNumber if available, otherwise by sortOrder
                const numA = parseInt(a.sectionNumber) || a.sortOrder || 0;
                const numB = parseInt(b.sectionNumber) || b.sortOrder || 0;
                return numA - numB;
              })
              .map((section, idx) => (
              <Reveal key={section.id || idx}>
                <motion.div
                  id={`sec-${idx}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  className="group bg-white rounded-2xl shadow-md border border-gray-100 hover:shadow-xl hover:border-[#008300]/30 transition-all duration-300 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="flex items-center gap-4 px-6 pt-6 pb-4 border-b border-gray-50">
                    <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-[#008300]/10 text-[#008300] text-2xl group-hover:bg-[#008300] group-hover:text-white transition-all duration-300">
                      {getIcon(section.icon)}
                    </div>
                    <div>
                      <span className="text-xs font-medium text-[#008300] uppercase tracking-wider">
                        Section {section.sectionNumber || (idx + 1)}
                      </span>
                      <h2 className="text-lg sm:text-xl font-bold text-gray-800 leading-tight">
                        {section.title}
                      </h2>
                    </div>
                  </div>

                  {/* Card Body with HTML rendering */}
                  <div className="px-6 py-5">
                    <div
                      className="prose max-w-none text-gray-600 leading-relaxed text-sm sm:text-base text-justify space-y-4"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </div>
                </motion.div>
              </Reveal>
            ))}

            {/* Dynamic Contact Details Card at the end */}
            {contactInfo && (
              <Reveal>
                <motion.div
                  id="contact-info-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  className="group bg-white rounded-2xl shadow-md border border-gray-100 hover:shadow-xl hover:border-[#008300]/30 transition-all duration-300 overflow-hidden"
                >
                  <div className="flex items-center gap-4 px-6 pt-6 pb-4 border-b border-gray-50">
                    <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-[#008300]/10 text-[#008300] text-2xl group-hover:bg-[#008300] group-hover:text-white transition-all duration-300">
                      <MdContactMail />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-[#008300] uppercase tracking-wider">
                        Support Info
                      </span>
                      <h2 className="text-lg sm:text-xl font-bold text-gray-800 leading-tight">
                        Contact Information
                      </h2>
                    </div>
                  </div>

                  <div className="px-6 py-5">
                    <p className="text-gray-600 mb-4 text-sm sm:text-base">
                      If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {contactInfo.email && (
                        <div className="p-4 rounded-xl bg-[#f0faf0] border border-[#008300]/15">
                          <p className="text-xs font-semibold text-[#008300] uppercase tracking-wide mb-1">Email</p>
                          <a href={`mailto:${contactInfo.email}`} className="text-sm text-gray-700 hover:text-[#008300] hover:underline transition">
                            {contactInfo.email}
                          </a>
                        </div>
                      )}
                      {contactInfo.phone && (
                        <div className="p-4 rounded-xl bg-[#f0faf0] border border-[#008300]/15">
                          <p className="text-xs font-semibold text-[#008300] uppercase tracking-wide mb-1">Phone</p>
                          <a href={`tel:${contactInfo.phone}`} className="text-sm text-gray-700 hover:text-[#008300] hover:underline transition">
                            {contactInfo.phone}
                          </a>
                        </div>
                      )}
                      {contactInfo.address && (
                        <div className="p-4 rounded-xl bg-[#f0faf0] border border-[#008300]/15">
                          <p className="text-xs font-semibold text-[#008300] uppercase tracking-wide mb-1">Address</p>
                          <p className="text-sm text-gray-700">{contactInfo.address}</p>
                        </div>
                      )}
                      {contactInfo.businessHours && (
                        <div className="p-4 rounded-xl bg-[#f0faf0] border border-[#008300]/15">
                          <p className="text-xs font-semibold text-[#008300] uppercase tracking-wide mb-1">Business Hours</p>
                          <p className="text-sm text-gray-700">{contactInfo.businessHours}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              </Reveal>
            )}
          </div>

          {/* Bottom CTA */}
          <Reveal>
            <div className="mt-16 text-center p-8 rounded-3xl bg-gradient-to-br from-[#008300] to-[#005a00] text-white shadow-2xl">
              <h3 className="text-xl sm:text-2xl font-bold mb-3">
                Have Questions About Your Privacy?
              </h3>
              <p className="text-white/85 mb-6 max-w-xl mx-auto text-sm sm:text-base">
                If you have any questions or concerns about how we handle your data, our team is here to help.
              </p>
              <Link
                to="/contact"
                className="inline-block px-8 py-3 rounded-full bg-white text-[#008300] font-semibold text-sm sm:text-base hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Contact Us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <AutoInternalLinks />
    </>
  );
};

export default PrivacyPolicy;

